import React, { useEffect, useReducer, useRef } from 'react';

import classNames from 'classnames';
import { validEmailRegex } from 'constants/regex';
import { produce } from 'immer';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import {
  emailProjectPageInitialStore,
  emailProjectPageReducer,
} from 'reducers/emailProjectPageReducer';
import voca from 'voca';

import useRequestError from 'components/hooks/useRequestError';

import CheckboxField from 'components/application/CheckboxField';
import ErrorMessage from 'components/application/ErrorMessage';
import LimitedTextField from 'components/application/LimitedTextField';
import ListField from 'components/application/ListField';
import ListSelect from 'components/application/ListSelect';
import RichTextEditor from 'components/application/RichTextEditor';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import MessageFooter from 'components/MessageFooter';

import { addDays, formatDate } from 'components/helpers/dates';
import { populateResources } from 'components/helpers/storeHelpers';

const subjectMaxLength = 75;

export default function EmailProjectPage({
  message,
  previouslyUsedEmailAddresses = [],
  projectId,
  projectName,
  projectPersonnelExist,
  projectPersonnelTabHidden,
  showPoweredBy,
  trainingRegisterAllowed,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const bodyRef = useRef();

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.EmailProjectPage',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const initialStore = produce(emailProjectPageInitialStore, (draftState) => {
    draftState.domain.message.data = message.data;
    populateResources(draftState, message.included);

    const user =
      draftState.domain.userCollection.data[
        draftState.domain.message.data.relationships.user.data.id
      ];

    draftState.application.pending.message = false;
    draftState.application.form.previouslyUsed =
      previouslyUsedEmailAddresses.map((email) => ({ value: email }));
    draftState.application.form.sendToSelf = true;
    draftState.application.form.subject =
      message.data.attributes.subject.substring(0, subjectMaxLength);
    draftState.application.form.ramsIncluded =
      message.data.attributes.ramsIncluded ||
      !message.data.attributes.trainingReportIncluded;
    draftState.application.form.trainingReportIncluded =
      message.data.attributes.trainingReportIncluded;
    draftState.application.form.evidenceIncluded =
      message.data.attributes.trainingReportIncluded;
    draftState.application.form.message = i18nT('defaultMessage', {
      projectName,
      userFirstName: user.attributes.firstName,
    });
  });

  const [store, dispatch] = useReducer(emailProjectPageReducer, initialStore);

  const user =
    !store.application.pending.message &&
    store.domain.userCollection.data[
      store.domain.message.data.relationships.user.data.id
    ];
  const version =
    !store.application.pending.message &&
    store.domain.versionCollection.data[
      store.domain.message.data.relationships.version.data.id
    ];

  const formToValues = store.application.form.to.map(
    (dispatch) => dispatch.value,
  );

  const selectableItems = store.application.form.previouslyUsed.filter(
    (pu) => !formToValues.includes(pu.value),
  );

  const handleInputChange = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;

    dispatch({
      type: 'UPDATE_FORM_FIELD',
      payload: { name: target.name, value },
    });
  };

  const handlePasswordRequiredInputChange = (event) => {
    removeErrorStyling({ target: { name: 'password' } });

    handleInputChange(event);
  };

  const handleListChange = ({ newItems }, { target }) => {
    dispatch({
      type: 'UPDATE_FORM_FIELD',
      payload: { name: target.name, value: newItems },
    });
  };

  const handleEditorChange = ({ content, target }) => {
    dispatch({
      type: 'UPDATE_FORM_FIELD',
      payload: { name: target.name, value: content },
    });
  };

  const isValidListValue = ({ value }) =>
    validEmailRegex.test(value.toLowerCase());

  useEffect(() => {
    if (submitDisabled) {
      bodyRef.current.scrollIntoView();
    }
  }, [submitDisabled]);

  return (
    !store.application.pending.message && (
      <>
        <div className='app-form__container'>
          <div
            className='app-form__header app-form__header--margin'
            ref={bodyRef}
          >
            <ErrorMessage
              isFallback={requestError.isFallback}
              validationErrors={requestError.validationErrors}
              wrapperClassName='app-form__error'
            />
            <h2 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {i18nT('title', {
                interpolation: { escapeValue: false },
                singularRAMSName,
              })}
            </h2>
          </div>
          <div className='app-form tw-border-grey-100 tw-bg-white'>
            <label className='app-form__label tw-mb-2 tw-text-m tw-font-medium'>
              {i18nTCommon('shared.from')}
            </label>
            <div className='tw-mb-10'>
              <span className='app-form__text'>{user.attributes.email}</span>
            </div>
            <label
              className={classNames(
                `app-form__label tw-mb-2 tw-text-m tw-font-medium after:tw-absolute after:tw-right-0 after:tw-font-normal after:tw-content-['${i18nTCommon('shared.required')}']`,
                requestError.validationErrors.to?.fieldHighlighted ?
                  'after:tw-text-red-600'
                : 'after:tw-text-grey-500',
              )}
            >
              {i18nTCommon('shared.to')}
            </label>
            <div className='tw-mb-10'>
              <ListField
                fieldError={requestError.validationErrors.to}
                isValidValue={isValidListValue}
                items={store.application.form.to}
                name='to'
                onChange={handleListChange}
                removeErrorStyling={removeErrorStyling}
              />
              {selectableItems.length > 0 && (
                <div className='tw-mt-4'>
                  <label className='app-form__label tw-mb-2 tw-text-m tw-font-medium'>
                    {i18nT('sections.previouslyUsed')}
                  </label>
                  <div>
                    <ListSelect
                      field='to'
                      items={store.application.form.to}
                      onChange={handleListChange}
                      removeErrorStyling={removeErrorStyling}
                      selectableItems={store.application.form.previouslyUsed}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='tw-mb-10'>
              <div className='tw-inline-block'>
                <CheckboxField
                  checked={store.application.form.sendToSelf}
                  label={i18nT('sendToSelf.label', {
                    email: user.attributes.email,
                    interpolation: { escapeValue: false },
                  })}
                  name='sendToSelf'
                  onChange={handleInputChange}
                  value='sendToSelf'
                />
              </div>
            </div>
            <label
              className={classNames(
                `app-form__label tw-mb-2 tw-text-m tw-font-medium after:tw-absolute after:tw-right-0 after:tw-font-normal after:tw-content-['${i18nTCommon('shared.required')}']`,
                requestError.validationErrors.subject?.fieldHighlighted ?
                  'after:tw-text-red-600'
                : 'after:tw-text-grey-500',
              )}
            >
              {i18nTCommon('shared.subject')}
            </label>
            <div className='tw-mb-10'>
              <LimitedTextField
                fieldError={requestError.validationErrors.subject}
                maxLength={subjectMaxLength}
                name='subject'
                onChange={handleInputChange}
                removeErrorStyling={removeErrorStyling}
                value={store.application.form.subject}
              />
            </div>
            <label className='app-form__label tw-mb-4 tw-text-m tw-font-medium'>
              {i18nTCommon('shared.documents')}
            </label>
            <div className='tw-mb-10'>
              <div className='tw-inline-block'>
                <CheckboxField
                  checked={store.application.form.ramsIncluded}
                  disabled={!store.application.form.trainingReportIncluded}
                  label={
                    version.attributes.displayNumber === undefined ?
                      voca.capitalize(singularRAMSName)
                    : i18nTCommon('projects.projectVersionNumber', {
                        interpolation: { escapeValue: false },
                        singularRAMSName: voca.capitalize(singularRAMSName),
                        versionNumber: version.attributes.displayNumber,
                      })
                  }
                  labelProps={{
                    className: 'hhq-checkbox__label--no-disabled-state',
                  }}
                  name='ramsIncluded'
                  onChange={handleInputChange}
                  tooltip={
                    !store.application.form.trainingReportIncluded &&
                    i18nTCommon('projects.atLeast1DocumentMustBeIncluded')
                  }
                  tooltipType='hover'
                  value='ramsIncluded'
                />
              </div>
              {trainingRegisterAllowed && (
                <div className='tw-mt-5'>
                  <div className='tw-inline-block'>
                    <CheckboxField
                      checked={store.application.form.trainingReportIncluded}
                      disabled={
                        !store.application.form.ramsIncluded ||
                        !projectPersonnelExist ||
                        projectPersonnelTabHidden
                      }
                      label={i18nTCommon('training.trainingReport')}
                      labelProps={
                        !projectPersonnelExist || projectPersonnelTabHidden ?
                          {
                            className:
                              'hhq-checkbox__container--disabled-inactive',
                          }
                        : {
                            className: 'hhq-checkbox__label--no-disabled-state',
                          }
                      }
                      name='trainingReportIncluded'
                      onChange={handleInputChange}
                      tooltip={
                        !store.application.form.ramsIncluded ?
                          i18nTCommon('projects.atLeast1DocumentMustBeIncluded')
                        : !projectPersonnelExist || projectPersonnelTabHidden ?
                          i18nT('trainingReportIncluded.tooltip.noPersonnel', {
                            interpolation: { escapeValue: false },
                            singularRAMSName,
                          })
                        : undefined
                      }
                      tooltipType='hover'
                      value='trainingReportIncluded'
                    />
                    {store.application.form.trainingReportIncluded && (
                      <div className='tw-ml-8 tw-mt-5 tw-inline-block'>
                        <CheckboxField
                          checked={store.application.form.evidenceIncluded}
                          label={i18nTCommon('shared.includeEvidence')}
                          name='evidenceIncluded'
                          onChange={handleInputChange}
                          value='evidenceIncluded'
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <label className='app-form__label tw-mb-4 tw-text-m tw-font-medium'>
              {i18nTCommon('shared.message')}
            </label>
            <div className='tw-mb-10'>
              <RichTextEditor
                name='message'
                onChange={handleEditorChange}
                options={{ minHeight: 230, toolbar: 'bold italic | bullist' }}
                value={store.application.form.message}
              />
            </div>
            <label className='app-form__label tw-mb-4 tw-text-m tw-font-medium'>
              {i18nT('sections.security')}
            </label>
            <div className='tw-mb-10'>
              <div className='tw-inline-block'>
                <CheckboxField
                  checked={store.application.form.passwordRequired}
                  label={i18nT('passwordRequired.label')}
                  name='passwordRequired'
                  onChange={handlePasswordRequiredInputChange}
                  value='passwordRequired'
                />
              </div>
              {store.application.form.passwordRequired && (
                <div
                  className={classNames(
                    'tw-max-w-300 tw-ml-8 tw-mt-4',
                    requestError.validationErrors.password &&
                      requestError.validationErrors.password.fieldHighlighted &&
                      'field__invalid',
                  )}
                >
                  <input
                    className='app-form__input-text tw-mb-2 tw-border-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300'
                    name='password'
                    onChange={handleInputChange}
                    onFocus={removeErrorStyling}
                    type='text'
                    value={store.application.form.password}
                  />
                  <span className='app-form__text--sub tw-text-s tw-font-normal tw-tracking-wide'>
                    <Trans
                      components={{
                        span: (
                          <span className='tw-text-s tw-font-medium tw-tracking-wide' />
                        ),
                      }}
                      i18nKey='pages.EmailProjectPage.password.disclaimer'
                      ns='components'
                    />
                  </span>
                </div>
              )}
            </div>
            <label className='app-form__label tw-mb-2 tw-text-m tw-font-medium'>
              {i18nT('expiry.label')}
            </label>
            <div>
              <span className='app-form__text'>
                <Trans
                  i18nKey='pages.EmailProjectPage.expiry.disclaimer'
                  ns='components'
                  values={{
                    expiryDate: formatDate({
                      date: addDays({ amount: 7, date: new Date() }),
                      format: 'd MMM yyyy',
                    }),
                  }}
                />
              </span>
            </div>
            {showPoweredBy && (
              <>
                <hr className='tw-my-10' />
                <label className='app-form__label tw-mb-4 tw-text-m tw-font-medium'>
                  {i18nTCommon('shared.poweredByHandsHQ')}
                </label>
                <div className='tw-mb-2'>
                  <div className='tw-inline-block'>
                    <CheckboxField
                      checked={true}
                      disabled={true}
                      label={i18nT('poweredByHandshq.label')}
                      labelProps={{
                        className: 'hhq-checkbox__label--no-disabled-state',
                      }}
                      name='poweredByHandshq'
                      onChange={() => {}}
                      value='poweredByHandshq'
                    />
                  </div>
                </div>
                <div className='tw-ml-8'>
                  <Trans
                    components={{
                      a: (
                        /* eslint-disable jsx-a11y/anchor-has-content */
                        <a
                          className='app-link tw-align-baseline tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                          href='https://intercom.help/hands-hq/en/articles/5058888-customise-the-look-branding-of-your-emails-remove-powered-by-handshq'
                          target='blank'
                        />
                        /* eslint-enable jsx-a11y/anchor-has-content */
                      ),
                      span: (
                        <span className='app-form__text--sub tw-text-s tw-font-normal tw-tracking-wide' />
                      ),
                    }}
                    i18nKey='pages.EmailProjectPage.poweredByHandshq.disclaimer'
                    ns='components'
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <MessageFooter
          data={store.application}
          isSubmitDisabled={submitDisabled}
          onRequestError={handleRequestError}
          projectId={projectId}
          resetRequestError={resetRequestError}
          versionId={message.data.relationships.version.data.id}
        />
      </>
    )
  );
}

EmailProjectPage.propTypes = {
  message: PropTypes.object.isRequired,
  previouslyUsedEmailAddresses: PropTypes.array,
  projectPersonnelExist: PropTypes.bool.isRequired,
  trainingRegisterAllowed: PropTypes.bool.isRequired,
};
